/**
 * Deal rules for the Home "Tasks" tile (pilot / Core + Procure Home, 2026-11-04).
 *
 * WHY. Today's plan only ever asked the rep to contact people. The deals they are working —
 * the thing returning users actually come back for — never produced a task, so a bid closing
 * in two days with no decision on it, or a pursued tender whose deadline passed with no outcome,
 * sat silently in My Deals. These rules turn that state into tasks.
 *
 * Built on the FE for now, from the same rows the Live Deals tile already loads (procurement
 * live + recently_closed, fit light, my-tenders augment). Pure functions, no fetching: the
 * caller passes the deals and the status map, so the rules are testable on their own.
 *
 * The three rules, most urgent first:
 *   record_outcome  — status reviewing/pursuing/submitted and the deadline has passed.
 *   mark_submitted  — status pursuing and the deadline is within MARK_SUBMITTED_DAYS.
 *   set_status      — Strong/Moderate Fit live deal, no status, deadline within SET_STATUS_DAYS (3).
 */
import { format } from 'date-fns';
import { getDeadlineHorizon } from '@/utils/procurementDealUtils';
import { resolveDealId } from '@/lib/api/myTenders';

// 3, not 7 (Tiago 2026-11-04): at 7 days every open tender raised a task and flooded the Home tile.
export const SET_STATUS_DAYS = 3;
export const MARK_SUBMITTED_DAYS = 3;
export const GOOD_FIT_LABELS = new Set(['Strong Fit', 'Moderate Fit']);

const OPEN_STATUSES = new Set(['reviewing', 'pursuing', 'submitted']);

export function dealDeadline(deal) {
  const dl = deal?.deadline_date || deal?.deadline || deal?.due_date || deal?.closing_date || null;
  if (!dl || dl === 'N/A' || (typeof dl === 'string' && !dl.trim())) return null;
  return dl;
}

/** "6 Nov 2026"; the unparseable value as-is; "No deadline" when there is none. */
export function formatDeadline(dl) {
  if (!dl) return 'No deadline';
  const d = new Date(dl);
  return Number.isNaN(d.getTime()) ? String(dl) : format(d, 'd MMM yyyy');
}

export function daysUntilDeadline(deal) {
  const dl = dealDeadline(deal);
  if (!dl) return null;
  const { days } = getDeadlineHorizon(dl);
  return Number.isFinite(days) ? days : null;
}

export function isGoodFitDeal(deal) {
  return GOOD_FIT_LABELS.has(deal?.fit_light_label);
}

/**
 * @param {object[]} deals live + recently-closed rows (each may appear once)
 * @param {Record<string, {status?: string|null}>} augment my-tenders records by deal id
 * @returns {{ id, rule, dealId, deal, status, days }[]} most urgent first
 */
export function buildDealTasks(deals, augment = {}) {
  const seen = new Set();
  const out = [];
  for (const deal of deals || []) {
    const dealId = resolveDealId(deal);
    if (!dealId || seen.has(dealId)) continue;
    seen.add(dealId);
    const status = augment?.[dealId]?.status || null;
    const days = daysUntilDeadline(deal);
    if (days == null) continue;
    if (status && OPEN_STATUSES.has(status) && days < 0) {
      out.push({ id: `record_outcome:${dealId}`, rule: 'record_outcome', dealId, deal, status, days });
    } else if (status === 'pursuing' && days >= 0 && days <= MARK_SUBMITTED_DAYS) {
      out.push({ id: `mark_submitted:${dealId}`, rule: 'mark_submitted', dealId, deal, status, days });
    } else if (!status && days >= 0 && days <= SET_STATUS_DAYS && isGoodFitDeal(deal)
      && String(deal?.status || 'live').toLowerCase() === 'live') {
      out.push({ id: `set_status:${dealId}`, rule: 'set_status', dealId, deal, status, days });
    }
  }
  const rank = { record_outcome: 0, mark_submitted: 1, set_status: 2 };
  return out.sort((a, b) => (rank[a.rule] - rank[b.rule]) || (a.days - b.days));
}
